// @flow

import omit from "lodash/omit";

import { bubaClient } from "buba-components/src/api";
import endpoints from "buba-components/src/api/endpoints";
import { enhance, authenticate as buildAuthenticate } from "buba-components/src/api/enhancers";

import { denormalizeJsonApi } from "buba-components/src/utils/denormalizeJsonApi";
import {
  pollWithRetries,
  defaultMaxRetriesCallback,
  type StatusCheckOptions
} from "buba-components/src/utils/polling";
import { store } from "../store";
import { type BankOfferStatus } from "../data/types/bankOffers";
import type { InquiryStatus } from "../data/types/inquiries";

const authenticate = buildAuthenticate(store);

// TODO(v1-removal): Used only by V1 InquiriesList bankAdvisor branch. Delete after grace period.
export const getBankAdvisorInquiries = (payload: {
  count_by_status?: boolean,
  status: InquiryStatus,
  page?: number,
  per_page?: number,
  sortBy?: string,
  sortDirection?: "asc" | "desc"
}) => {
  const apiPayload = {
    count_by_status: payload.count_by_status,
    "filters[general_status]": payload.status,
    page: payload.page,
    per_page: payload.per_page,
    ...(payload.sortBy && payload.sortDirection
      ? {
          [`sort[${payload.sortBy}]`]: payload.sortDirection
        }
      : {})
  };

  return bubaClient(
    enhance(authenticate)({
      validateStatus: status => status === 200,
      method: "get",
      url: endpoints.bankAdvisorInquiries(),
      params: apiPayload
    })
  );
};

export const getUserData = () =>
  bubaClient(
    enhance(authenticate)({
      validateStatus: status => status === 200,
      method: "get",
      url: endpoints.getUserData()
    })
  );

export const updateUserData = (payload: {
  first_name: string,
  last_name: string,
  position?: string,
  phone_number?: string
}) =>
  bubaClient(
    enhance(authenticate)({
      validateStatus: status => status === 200,
      method: "patch",
      url: endpoints.getUserData(),
      data: payload
    })
  );

export const getBankOffers = (payload: {
  status?: BankOfferStatus,
  page?: number,
  per_page?: number,
  offset?: number,
  count_by_status?: boolean,
  sortBy?: string,
  sortDirection?: "asc" | "desc",
  creator: "customer" | "bank_advisor"
}) => {
  const finalPayload = {
    ...payload,
    "filters[general_status]": payload.status,
    "filters[creator_type]": payload.creator,
    ...(payload.sortBy && payload.sortDirection
      ? { [`sort[${payload.sortBy}]`]: payload.sortDirection }
      : {})
  };
  delete finalPayload.status;
  delete finalPayload.sortBy;
  delete finalPayload.sortDirection;

  return bubaClient(
    enhance(authenticate)({
      validateStatus: status => status === 200,
      method: "get",
      url: endpoints.bankOffers(),
      headers: {},
      params: finalPayload
    })
  );
};
export const getBankHints = (payload: { id: number }) =>
  bubaClient(
    enhance(authenticate)({
      validateStatus: status => status === 200,
      method: "get",
      url: endpoints.hints(payload.id)
    })
  );

export const getInquiryProcessFiles = (payload: { id: number }) =>
  bubaClient(
    enhance(authenticate)({
      validateStatus: status => status === 200,
      method: "get",
      url: endpoints.files(payload.id)
    })
  );

export const getBankOfferFiles = (payload: { id: number }) =>
  bubaClient(
    enhance(authenticate)({
      validateStatus: status => status === 200,
      method: "get",
      url: endpoints.filesBankOffer(payload.id)
    })
  );

export const getFilesRequests = (payload: { id: number }) =>
  bubaClient(
    enhance(authenticate)({
      validateStatus: status => status === 200,
      method: "get",
      url: endpoints.filesRequests(payload.id)
    })
  );

export const getInquiryProcessFilesAndRequests = (payload: { inquiryProcessId: number }) =>
  Promise.all([
    getInquiryProcessFiles({ id: payload.inquiryProcessId }),
    getFilesRequests({ id: payload.inquiryProcessId })
  ]);

export const getAllFiles = (payload: { bankOfferId: number, inquiryProcessId: number }) =>
  Promise.all([
    getBankOfferFiles({ id: payload.bankOfferId }),
    getInquiryProcessFiles({ id: payload.inquiryProcessId }),
    getFilesRequests({ id: payload.inquiryProcessId })
  ]);

export const getBankOffer = (payload: { id: number }) =>
  bubaClient(
    enhance(authenticate)({
      validateStatus: status => status === 200,
      method: "get",
      url: endpoints.getBankOffer(payload.id)
    })
  );

export const setStatusOpen = (id: number) =>
  bubaClient(
    enhance(authenticate)({
      validateStatus: status => status === 204,
      method: "post",
      url: endpoints.setBankOfferStatusOpen(id)
    })
  );

export const getAndOpenBankOffer = (payload: { id: number }) =>
  getBankOffer(payload)
    .then(bankOfferResponse => {
      const bankOfferId = bankOfferResponse.data.data.id;

      if (bankOfferResponse.data.data.attributes.status === "new") {
        return Promise.all([bankOfferResponse, setStatusOpen(bankOfferId)]);
      }
      return null;
    })
    .then(() => getBankOffer({ id: payload.id }));

export const getCompany = (payload: { id: number }) =>
  bubaClient(
    enhance(authenticate)({
      validateStatus: status => status === 200,
      method: "get",
      url: endpoints.company(payload.id)
    })
  );

export const checkFileStatus = (payload: { id: number }, options: StatusCheckOptions = {}) => {
  const apiCall = () =>
    bubaClient(
      enhance(authenticate)({
        validateStatus: status => status === 200,
        method: "get",
        url: endpoints.fileStatus(payload.id)
      })
    );

  const defaultCallback = (retryCount: number) =>
    defaultMaxRetriesCallback(retryCount, options.addMessage, "Datei-Verarbeitung");

  return pollWithRetries(apiCall, {
    continueStatuses: ["pending"],
    maxRetries: options.maxRetries,
    onMaxRetriesReached: options.onMaxRetriesReached || defaultCallback,
    extractStatus: res => {
      const { status } = denormalizeJsonApi(res);
      return status;
    }
  }).then(res => {
    const { status } = denormalizeJsonApi(res);
    return Promise.resolve({ status });
  });
};

export const downloadFile = (payload: { id: number }) =>
  bubaClient(
    enhance(authenticate)({
      url: endpoints.file(payload.id),
      headers: {
        Accept: "application/pdf"
      },
      responseType: "arraybuffer"
    })
  );

export const downloadOkpSummary = (okpUid: string) =>
  bubaClient(
    enhance(authenticate)({
      url: endpoints.downloadOkpSummary(okpUid),
      headers: {
        Accept: "application/pdf"
      },
      responseType: "arraybuffer"
    })
  );

export const createFileBundle = (payload: { uploaded_file_ids: Array<number> }) =>
  bubaClient(
    enhance(authenticate)({
      validateStatus: status => status === 201,
      url: endpoints.createFileBundle(),
      method: "post",
      data: payload
    })
  );

export const checkFileBundleStatus = (
  payload: { id: number },
  options: StatusCheckOptions = {}
) => {
  const apiCall = () =>
    bubaClient(
      enhance(authenticate)({
        validateStatus: status => status === 200,
        url: endpoints.fileBundleStatus(payload.id),
        method: "get"
      })
    );

  const defaultCallback = (retryCount: number) =>
    defaultMaxRetriesCallback(retryCount, options.addMessage, "Datei-Bundle-Verarbeitung");

  return pollWithRetries(apiCall, {
    continueStatuses: ["scheduled"],
    maxRetries: options.maxRetries,
    onMaxRetriesReached: options.onMaxRetriesReached || defaultCallback,
    extractStatus: res => {
      const { status } = denormalizeJsonApi(res);
      return status;
    }
  }).then(res => {
    const { status } = denormalizeJsonApi(res);
    return Promise.resolve({ id: payload.id, status });
  });
};

export const downloadFileBundle = (payload: { id: number }) =>
  bubaClient(
    enhance(authenticate)({
      url: endpoints.fileBundle(payload.id),
      headers: {
        Accept: "application/zip"
      },
      responseType: "arraybuffer"
    })
  );

export const requestAndDownloadFileBundle = (
  payload: { uploaded_file_ids: Array<number> },
  options: StatusCheckOptions = {}
) =>
  createFileBundle(payload)
    .then(res => {
      const { id } = denormalizeJsonApi(res);

      return checkFileBundleStatus({ id }, options);
    })
    .then(res => {
      const { id } = res;

      return downloadFileBundle({ id });
    });

export const getInquiry = (payload: { id: number }) =>
  bubaClient(
    enhance(authenticate)({
      validateStatus: status => status === 200,
      method: "get",
      url: endpoints.inquiry(payload.id)
    })
  );

export const requestFile = (payload: { id: number, description: string }) =>
  bubaClient(
    enhance(authenticate)({
      validateStatus: status => status === 201,
      method: "post",
      url: endpoints.filesRequests(payload.id),
      params: omit(payload, "id")
    })
  );

export const uploadFile = (
  payload: {
    id: number,
    file: File,
    file_upload_request_id?: number
  },
  url: string
) => {
  const formData = new FormData();
  formData.append("file", payload.file);

  if (payload.file_upload_request_id) {
    formData.append("file_upload_request_id", payload.file_upload_request_id);
  }

  return bubaClient(
    enhance(authenticate)({
      validateStatus: status => status === 201,
      method: "post",
      headers: {
        "Content-Type": "multipart/form-data",
        Accept: "application/vnd.api+json"
      },
      data: formData,
      url
    })
  );
};

export const uploadAndCheckFileForCustomer = (
  payload: {
    id: number,
    file: File,
    file_upload_request_id?: number
  },
  options: StatusCheckOptions = {}
) =>
  uploadFile(payload, endpoints.filesBankOffer(payload.id)).then(res => {
    const { id } = denormalizeJsonApi(res);
    return checkFileStatus({ id }, options);
  });

export const getInquiryFromBankOffer = (payload: { id: number }) => {
  return getAndOpenBankOffer(payload).then(bankOfferResponse => {
    const bankOffer = denormalizeJsonApi(bankOfferResponse);

    if (!bankOffer) {
      return Promise.reject();
    }

    const inquiryId = Number(bankOffer.inquiry.id);
    const companyId = Number(bankOffer.company.id);
    const additionalInfo = {
      inquiryCreator: bankOffer.inquiryCreator,
      createdAt: bankOffer.createdAt
    };

    return Promise.all([
      getInquiry({ id: inquiryId }),
      getCompany({ id: companyId }),
      additionalInfo
    ]);
  });
};

// TODO(v1-removal): Used only by V1 BankInquiryDetails branch. Delete after grace period.
export const getDetailsInformationForBankAdvisorInquiry = (payload: { id: number }) => {
  return getInquiry(payload).then(inquiryResponse => {
    const inquiry = denormalizeJsonApi(inquiryResponse);
    if (!inquiry) {
      return Promise.reject();
    }

    const companyId = Number(inquiry.company.id);
    const additionalInfo = {
      createdAt: inquiry.createdAt
    };

    return Promise.all([inquiryResponse, getCompany({ id: companyId }), additionalInfo, null]);
  });
};

export const setStatusContractDone = (id: number) =>
  bubaClient(
    enhance(authenticate)({
      validateStatus: status => status === 204,
      method: "post",
      url: endpoints.setBankOfferStatusContractDone(id)
    })
  );

export const setStatusSubmitOffer = (id: number) =>
  bubaClient(
    enhance(authenticate)({
      validateStatus: status => status === 204,
      method: "post",
      url: endpoints.setBankOfferStatusSubmitOffer(id)
    })
  );

export const setStatusDeclineByBank = (id: number) =>
  bubaClient(
    enhance(authenticate)({
      validateStatus: status => status === 204,
      method: "post",
      url: endpoints.setBankOfferStatusDeclineByBank(id)
    })
  );

export const setStatusDeclineByCustomer = (id: number) =>
  bubaClient(
    enhance(authenticate)({
      validateStatus: status => status === 204,
      method: "post",
      url: endpoints.setBankOfferStatusDeclineByCustomer(id)
    })
  );

export const setStatusSubmitInterest = (id: number) =>
  bubaClient(
    enhance(authenticate)({
      validateStatus: status => status === 204,
      method: "post",
      url: endpoints.setBankOfferStatusSubmitInterest(id)
    })
  );

export const uploadBankOfferFile = (payload: { id: number, file: File }) => {
  const formData = new FormData();
  formData.append("file", payload.file);

  return bubaClient(
    enhance(authenticate)({
      validateStatus: status => status === 201,
      method: "post",
      headers: {
        "Content-Type": "multipart/form-data",
        Accept: "application/vnd.api+json"
      },
      data: formData,
      url: endpoints.bankOfferFile(payload.id)
    })
  );
};

export const getBankOfferFile = (payload: { id: number }) =>
  bubaClient(
    enhance(authenticate)({
      validateStatus: status => status === 200,
      method: "get",
      headers: {
        Accept: "application/vnd.api+json"
      },
      url: endpoints.bankOfferFile(payload.id)
    })
  );

export const uploadAndCheckFile = (
  payload: { id: number, file: File },
  options: StatusCheckOptions = {}
) =>
  uploadBankOfferFile(payload).then(res => {
    const normalized = denormalizeJsonApi(res);

    if (normalized) {
      const { id } = normalized;
      return checkFileStatus({ id }, options);
    }

    return Promise.reject();
  });

export const uploadAndCheckFileForGuaranteeAgent = (
  payload: {
    id: number,
    file: File,
    file_upload_request_id?: number
  },
  options: StatusCheckOptions = {}
) =>
  uploadFile(payload, endpoints.filesInquiryProcess(payload.id)).then(res => {
    const { id } = denormalizeJsonApi(res);
    return checkFileStatus({ id }, options);
  });

export const changePassword = (payload: {
  oldPassword: string,
  password: string,
  passwordConfirmation: string
}) =>
  bubaClient(
    enhance(authenticate)({
      validateStatus: status => status === 204,
      method: "patch",
      url: endpoints.changePassword(),
      data: payload
    })
  );
